import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { userRequest } from '../utils/requestMethods';
import { BackButton } from '../components';
import { useStateContext } from '../contexts/ContextProvider';
import { formatter } from '../utils/currencyUtils';
import {
  FiCheckCircle, FiClock, FiLoader, FiXCircle,
  FiUser, FiHash, FiArrowUpRight, FiCalendar,
  FiBriefcase, FiCreditCard
} from 'react-icons/fi';

const statusConfig = {
  successful: {
    bg: 'bg-emerald-500',
    text: 'text-white',
    dot: 'bg-emerald-500',
    timelineText: 'text-emerald-700',
    timelineBg: 'bg-emerald-50',
    icon: FiCheckCircle,
    label: 'Successful',
  },
  success: {
    bg: 'bg-emerald-500',
    text: 'text-white',
    dot: 'bg-emerald-500',
    timelineText: 'text-emerald-700',
    timelineBg: 'bg-emerald-50',
    icon: FiCheckCircle,
    label: 'Successful',
  },
  completed: {
    bg: 'bg-emerald-500',
    text: 'text-white',
    dot: 'bg-emerald-500',
    timelineText: 'text-emerald-700',
    timelineBg: 'bg-emerald-50',
    icon: FiCheckCircle,
    label: 'Completed',
  },
  processing: {
    bg: 'bg-blue-500',
    text: 'text-white',
    dot: 'bg-blue-500',
    timelineText: 'text-blue-700',
    timelineBg: 'bg-blue-50',
    icon: FiLoader,
    label: 'Processing',
  },
  pending: {
    bg: 'bg-amber-400',
    text: 'text-white',
    dot: 'bg-amber-400',
    timelineText: 'text-amber-700',
    timelineBg: 'bg-amber-50',
    icon: FiClock,
    label: 'Pending',
  },
  failed: {
    bg: 'bg-red-500',
    text: 'text-white',
    dot: 'bg-red-500',
    timelineText: 'text-red-700',
    timelineBg: 'bg-red-50',
    icon: FiXCircle,
    label: 'Failed',
  },
};

const getStatusConfig = (status) =>
  statusConfig[status?.toLowerCase()] || statusConfig.pending;

const StatusBadge = ({ status }) => {
  const cfg = getStatusConfig(status);
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${cfg.bg} ${cfg.text}`}>
      <Icon size={14} />
      {cfg.label}
    </span>
  );
};

const DetailRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
    <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
      <Icon size={15} className="text-indigo-500" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-gray-800 break-words">{value || '—'}</p>
    </div>
  </div>
);

const SectionCard = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
    <div className="px-5 py-3.5 border-b border-gray-100">
      <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
    </div>
    <div className="px-5 py-1">{children}</div>
  </div>
);

const TransactionDetails = ({ history }) => {
  const { school } = useStateContext();
  const schoolId = school?._id;
  const location = useLocation();
  const transactionId = location.pathname.split('/')[2];

  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!schoolId || !transactionId) return;
    const fetchTransaction = async () => {
      try {
        setLoading(true);
        const { data } = await userRequest.get(`/transactions/school/${schoolId}/${transactionId}`);
        setTransaction(data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    fetchTransaction();
  }, [schoolId, transactionId]);

  const meta = transaction?.metadata || {};

  // Support both new flat format and old nested destination format
  const destAccountName = meta.destinationAccountName || meta.destination?.accountName;
  const destAccountNumber = meta.destinationAccountNumber || meta.destination?.accountNumber;
  const destBankCode = meta.destinationBankCode || meta.destination?.bank?.nipCode;
  const destBankName = meta.destination?.bank?.name;
  const narration = meta.narration || meta.reason;
  const requestedBy = meta.requestedByUserName || meta.requestedBy;

  const statusHistory = [...(transaction?.status_history || [])].reverse();
  const currentStatus = transaction?.status;
  const cfg = getStatusConfig(currentStatus);

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-NG', {
      dateStyle: 'medium', timeStyle: 'short', hour12: true,
    });
  };

  if (loading) {
    return (
      <div className="m-2 md:m-10 mt-24 p-4 md:p-10 bg-white rounded-3xl min-h-screen">
        <BackButton history={history} />
        <div className="flex justify-center items-center h-64">
          <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="m-2 md:m-10 mt-24 p-4 md:p-10 bg-white rounded-3xl min-h-screen">
      <BackButton history={history} />

      {transaction && (
        <div className="max-w-2xl mx-auto mt-6 space-y-4">

          {/* Hero card */}
          <div className={`rounded-2xl p-6 text-white bg-gradient-to-br from-indigo-600 to-indigo-800 shadow-lg`}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-indigo-200 text-xs font-medium uppercase tracking-widest mb-1">
                  {transaction.type?.replace('_', ' ')}
                </p>
                <p className="text-4xl font-bold tracking-tight">
                  {formatter.format(transaction.final_amount || transaction.amount || 0)}
                </p>
              </div>
              <StatusBadge status={currentStatus} />
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-indigo-200 pt-4 border-t border-indigo-500/40">
              <span className="flex items-center gap-1.5">
                <FiBriefcase size={13} />
                {transaction.school?.school_name}
              </span>
              <span className="flex items-center gap-1.5">
                <FiCalendar size={13} />
                {formatDate(transaction.createdAt)}
              </span>
            </div>
          </div>

          {/* Destination Account */}
          <SectionCard title="Destination Account">
            <DetailRow
              icon={FiUser}
              label="Account Name"
              value={destAccountName}
            />
            <DetailRow
              icon={FiCreditCard}
              label="Account Number"
              value={destAccountNumber}
            />
            <DetailRow
              icon={FiHash}
              label={destBankName ? `Bank (${destBankCode})` : 'Bank Code'}
              value={destBankName || destBankCode}
            />
          </SectionCard>

          {/* Transaction Details */}
          <SectionCard title="Transaction Details">
            <DetailRow
              icon={FiHash}
              label="Reference"
              value={transaction.reference}
            />
            <DetailRow
              icon={FiBriefcase}
              label="Narration / Purpose"
              value={narration}
            />
            {requestedBy && (
              <DetailRow
                icon={FiUser}
                label="Requested By"
                value={requestedBy}
              />
            )}
            <DetailRow
              icon={FiCalendar}
              label="Session / Term"
              value={transaction.session ? `${transaction.session} — Term ${transaction.term}` : null}
            />
            <DetailRow
              icon={FiArrowUpRight}
              label="Amount"
              value={formatter.format(transaction.amount || 0)}
            />
          </SectionCard>

          {/* Status History */}
          {statusHistory.length > 0 && (
            <SectionCard title="Status History">
              <div className="py-2 space-y-1">
                {statusHistory.map((entry, idx) => {
                  const entryCfg = getStatusConfig(entry.status);
                  const EntryIcon = entryCfg.icon;
                  const isLatest = idx === 0;
                  return (
                    <div key={entry._id} className="flex items-start gap-3 py-2.5">
                      <div className="flex flex-col items-center gap-1 pt-0.5">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${entryCfg.timelineBg}`}>
                          <EntryIcon size={14} className={entryCfg.timelineText} />
                        </div>
                        {idx < statusHistory.length - 1 && (
                          <div className="w-px h-4 bg-gray-200" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0 pb-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-sm font-semibold capitalize ${entryCfg.timelineText}`}>
                            {entryCfg.label}
                            {isLatest && (
                              <span className="ml-2 text-xs font-medium text-gray-400">(current)</span>
                            )}
                          </span>
                          <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
                            {formatDate(entry.timestamp)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          )}

        </div>
      )}
    </div>
  );
};

export default TransactionDetails;
